import { useEffect, useState } from "react";

/**
 * Estado da barra fixa de CTA da landing (sticky-cta.tsx).
 *
 * - `absent`: a página não tem o hero `#inicio`, então não existe barra
 *   (blog, posts). Nada precisa reservar espaço para ela.
 * - `hidden`: o hero está visível — a barra está recolhida.
 * - `visible`: o hero saiu da tela — a barra está na tela.
 */
export type StickyCtaState = "absent" | "hidden" | "visible";

/**
 * Fonte única do estado da barra fixa, compartilhada entre a própria barra e o
 * botão flutuante do WhatsApp. Os dois precisam entrar no mesmo instante: o
 * botão sobe para `bottom-24` justamente para não cobrir a barra, e sem esse
 * sincronismo ele ficava flutuando alto no começo da página, com espaço vazio
 * embaixo.
 *
 * Usa IntersectionObserver e não listener de scroll de propósito — um listener
 * não-passivo travava o scroll no iOS/WebKit.
 */
export function useStickyCtaState(): StickyCtaState {
  // Começa em `hidden` — o estado correto no topo da landing, que é onde a
  // página abre. Partir de `absent` faria o botão do WhatsApp piscar visível
  // por um frame antes de recolher. Em páginas sem hero o efeito corrige para
  // `absent` no mount e o botão só entra com o fade, sem salto.
  const [state, setState] = useState<StickyCtaState>("hidden");

  useEffect(() => {
    const hero = document.getElementById("inicio");
    if (!hero) {
      setState("absent");
      return;
    }

    // Observa o bloco de CTA do hero, não o hero inteiro. O hero termina só
    // depois do print do produto, que no mobile é altíssimo: observando ele,
    // a barra só entrava por volta dos 4.000px, e entre a saída do botão do
    // hero e essa marca a página ficava ~2,5 telas sem nenhum CTA na tela.
    // Com o bloco de CTA, a barra assume exatamente quando o botão some.
    const trigger = document.getElementById("hero-cta") ?? hero;

    const observer = new IntersectionObserver(
      ([entry]) => setState(entry.isIntersecting ? "hidden" : "visible"),
      { threshold: 0 },
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, []);

  return state;
}
